import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Check, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  downloadTrack,
  isDownloaded,
  offlineSupported,
  removeTrack,
} from '@/lib/offline-audio';

interface OfflineDownloadButtonProps {
  /** The tracks this meditation needs: voice and music are separate files. */
  urls: (string | null | undefined)[];
  onChange?: () => void;
  className?: string;
}

/**
 * Downloads the tracks behind whatever the listener has selected, so the
 * meditation plays without touching the network again.
 *
 * Hidden on the web: there is nowhere sensible to put tens of megabytes that
 * the user can later find and clear, and the button would promise something
 * the browser can't keep.
 */
export function OfflineDownloadButton({
  urls,
  onChange,
  className,
}: OfflineDownloadButtonProps) {
  const tracks = urls.filter((u): u is string => !!u);
  const trackKey = tracks.join('|');
  const [state, setState] = useState<'unknown' | 'absent' | 'present'>('unknown');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    if (!offlineSupported() || tracks.length === 0) return;
    const flags = await Promise.all(tracks.map(isDownloaded));
    setState(flags.every(Boolean) ? 'present' : 'absent');
  }, [trackKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!offlineSupported() || tracks.length === 0 || state === 'unknown') return null;

  const handleDownload = async () => {
    setBusy(true);
    try {
      for (let i = 0; i < tracks.length; i += 1) {
        setProgress(`${i + 1} van ${tracks.length}`);
        await downloadTrack(tracks[i]);
      }
      setState('present');
      onChange?.();
      toast({
        title: 'Klaar om offline te luisteren',
        description: 'Deze meditatie staat nu op je apparaat.',
      });
    } catch (err) {
      console.error('Offline download failed:', err);
      // Don't leave half a meditation behind: one track without the other
      // plays as silence under a voice, or a voice with no bed.
      await Promise.all(tracks.map(removeTrack));
      setState('absent');
      toast({
        variant: 'destructive',
        title: 'Downloaden mislukt',
        description: 'Controleer je verbinding en probeer het opnieuw.',
      });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await Promise.all(tracks.map(removeTrack));
      setState('absent');
      onChange?.();
      toast({ title: 'Van je apparaat verwijderd' });
    } finally {
      setBusy(false);
    }
  };

  if (state === 'present') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleRemove}
        disabled={busy}
        className={className}
        title="Verwijder van dit apparaat"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Check className="mr-2 h-4 w-4 text-green-600" />
            Offline beschikbaar
            <Trash2 className="ml-2 h-4 w-4 opacity-60" />
          </>
        )}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleDownload}
      disabled={busy}
      className={className}
    >
      {busy ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {progress ? `Downloaden ${progress}` : 'Downloaden...'}
        </>
      ) : (
        <>
          <Download className="mr-2 h-4 w-4" />
          Offline opslaan
        </>
      )}
    </Button>
  );
}
